import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import LoginActivityAlert from "@/components/LoginActivityAlert";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { ShoppingCart, Package, Truck, CheckCircle2, Wallet, Star, ShoppingBag, Heart, Gift, Sparkles } from "lucide-react";

function Kpi({ label, value, icon: Icon, tone = "text-primary", suffix = "" }) {
  return (
    <div className="surface-card p-5" data-testid={`dkpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="flex items-center justify-between"><span className="label-overline">{label}</span><Icon className={`w-4 h-4 ${tone}`} /></div>
      <div className="font-display text-2xl sm:text-3xl font-black mt-3">{value ?? "—"}{suffix && <span className="text-sm font-bold text-muted-foreground ml-1">{suffix}</span>}</div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [k, setK] = useState(null);
  useEffect(() => { api.get("/dealer/kpis").then(({ data }) => setK(data)); }, []);
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8" data-testid="dealer-dashboard">
      <div>
        <div className="label-overline">Dealer portal</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-2">{t("dashboards.dealer.welcome", { name: user?.full_name?.split(" ")[0] || "Dealer" })}</h1>
        <p className="text-muted-foreground mt-2">{t("dashboards.dealer.subtitle")}</p>
      </div>

      <LoginActivityAlert />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Total orders" value={k?.total_orders} icon={ShoppingBag} />
        <Kpi label="Pending orders" value={k?.pending_orders} icon={Package} tone="text-amber-600" />
        <Kpi label="In transit" value={k?.in_transit} icon={Truck} tone="text-violet-600" />
        <Kpi label="Delivered" value={k?.delivered} icon={CheckCircle2} tone="text-emerald-600" />
        <Kpi label="Outstanding balance" value={`₹${(k?.outstanding_balance || 0).toLocaleString()}`} icon={Wallet} tone="text-amber-700" />
        <Kpi label="Available credit" value={`₹${(k?.available_credit || 0).toLocaleString()}`} icon={Wallet} tone="text-emerald-700" />
        <Kpi label="Loyalty points" value={k?.loyalty_points} icon={Star} tone="text-violet-700" />
        <Kpi label="Cart items" value={k?.cart_items} icon={ShoppingCart} />
      </div>

      <div className="grid md:grid-cols-3 gap-3 text-sm">
        <Link to="/dealer/products"><Card className="hover:shadow-md transition-shadow"><CardContent className="p-4 flex items-center gap-3"><ShoppingBag className="w-5 h-5 text-primary" /><div><div className="font-semibold">Browse catalogue</div><div className="text-muted-foreground text-xs">Live stock and dealer pricing.</div></div></CardContent></Card></Link>
        <Link to="/dealer/wishlist"><Card className="hover:shadow-md transition-shadow"><CardContent className="p-4 flex items-center gap-3"><Heart className="w-5 h-5 text-primary" /><div><div className="font-semibold">Wishlist</div><div className="text-muted-foreground text-xs">Saved products you can reorder later.</div></div></CardContent></Card></Link>
        <Link to="/dealer/loyalty"><Card className="hover:shadow-md transition-shadow"><CardContent className="p-4 flex items-center gap-3"><Gift className="w-5 h-5 text-violet-600" /><div><div className="font-semibold">Loyalty <Badge variant="outline" className="ml-1">{k?.loyalty_points ?? 0} pts</Badge></div><div className="text-muted-foreground text-xs">Earn 2% on every paid order. Redeem at checkout.</div></div></CardContent></Card></Link>
      </div>

      <Card><CardContent className="p-4 text-xs text-muted-foreground flex items-center gap-2"><Sparkles className="w-4 h-4" /> Payments are processed live via Stripe (test mode). All in-app notifications work today; WhatsApp/Email/SMS will activate once you wire those providers under Integrations.</CardContent></Card>
    </div>
  );
}
